// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/OnboardingV2Page.tsx (frame: SidebarProvider + AppV3Sidebar + `onb-ds` wrapper + inset `main`) + ui/shadcn/sidebar.tsx (layout only)
import type { CSSProperties, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { INSET_PANEL_CLASS, INSET_PROVIDER_CLASS } from './shell-classes';

/** TP's SIDEBAR_WIDTH (sidebar.tsx). */
const SIDEBAR_WIDTH = '12rem';

export interface ShellFrameProps {
  /** The rail (BlurredAppSidebar). Hidden below `md`, like TP's. */
  sidebar: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * The onboarding framing without the data layer: the rail's ground, and the page as a rounded panel inset on
 * it. TP's page is the same three boxes: the provider (the rail's ground), the `onb-ds` wrapper (`h-dvh`,
 * which also scopes the running-step shimmer) and the inset `main`, a flex row for the chat and, later, the
 * Tasks aside. Presentational: no sidebar context, no notifications chrome.
 */
export function ShellFrame({ sidebar, className, children }: ShellFrameProps) {
  return (
    <div
      data-slot="sidebar-wrapper"
      style={{ '--sidebar-width': SIDEBAR_WIDTH } as CSSProperties}
      className={cn('group/sidebar-wrapper flex min-h-svh w-full', INSET_PROVIDER_CLASS)}
    >
      {/* The sidebar slot: peer of the panel, so `peer-data-[variant=inset]` selectors resolve as in TP. */}
      <div
        data-slot="sidebar"
        data-variant="inset"
        data-state="expanded"
        className="group peer hidden text-sidebar-foreground md:block"
      >
        <div data-slot="sidebar-gap" className="relative w-[var(--sidebar-width)] bg-transparent" />
        <div
          data-slot="sidebar-container"
          className="fixed inset-y-0 left-0 z-40 hidden h-svh w-[var(--sidebar-width)] p-2 md:flex"
        >
          {sidebar}
        </div>
      </div>
      <div className="onb-ds flex h-dvh min-w-0 flex-1 overflow-hidden text-foreground">
        <main data-slot="sidebar-inset" className={cn(INSET_PANEL_CLASS, 'relative flex min-w-0 flex-1', className)}>
          {children}
        </main>
      </div>
    </div>
  );
}
